import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/ledgerApi";
import { MiniMark } from "@/components/HeroLogo";
import { BakeOrderBuilder } from "@/components/ledger/BakeOrderBuilder";
import { MasterProducts } from "@/components/ledger/MasterProducts";
import { MenuPdfButton, RecipeBookButton } from "@/components/ledger/MenuPdf";
import { BakeHistory } from "@/components/ledger/BakeHistory";
import { Accounts } from "@/components/ledger/Accounts";
import {
  ArrowLeft,
  Loader2,
  Phone,
  StickyNote,
  Search,
  LogOut,
  Users,
  ScrollText,
  X,
  CalendarDays,
  PlusCircle,
  ListChecks,
  Wallet,
  ChefHat,
} from "lucide-react";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const labelCls =
  "font-label text-[9px] tracking-[0.22em] uppercase text-[rgba(240,233,216,0.5)]";
const inputCls =
  "w-full h-12 px-4 rounded-[12px] bg-[rgba(240,233,216,0.05)] border border-[rgba(179,145,74,0.35)] font-body text-sm text-[var(--hoa-cream-50)] placeholder:text-[rgba(240,233,216,0.35)] focus:outline-none focus:border-[var(--hoa-gold-400)] transition-colors";

/* ------------------------------------------------------------- Login */
function LoginView({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/login", { username, password });
      onLogin();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Incorrect username or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[420px] mt-16 sm:mt-24" data-testid="ledger-login-view">
      <div className="flex items-center gap-4">
        <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
          The Ledger
        </span>
        <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
      </div>
      <h1 className="font-display text-[var(--hoa-cream-50)] text-3xl sm:text-4xl mt-4">
        Private to the house.
      </h1>
      <p className="font-body text-[rgba(240,233,216,0.6)] text-sm mt-3">
        Sign in once — the ledger will remember you on this device.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div className="space-y-2">
          <label className={labelCls} htmlFor="ledger-username">Username</label>
          <input
            id="ledger-username"
            data-testid="ledger-login-username"
            className={inputCls}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
        </div>
        <div className="space-y-2">
          <label className={labelCls} htmlFor="ledger-password">Password</label>
          <input
            id="ledger-password"
            data-testid="ledger-login-password"
            className={inputCls}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        {error && (
          <p className="font-body text-sm text-[#c96f5a]" data-testid="ledger-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="ledger-login-submit"
          className="w-full h-12 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase hover:brightness-110 transition-[filter] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open the Ledger"}
        </button>
      </form>
    </div>
  );
}

/* --------------------------------------------------------- OrderCard */
function OrderCard({ order }) {
  return (
    <article
      data-testid={`order-card-${order.id}`}
      className="relative bg-[var(--hoa-cream-50)] rounded-[var(--hoa-radius-lg)] shadow-[var(--hoa-shadow-card)] p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-[var(--hoa-ink-950)] text-lg sm:text-xl">{order.customer_name}</h2>
        <time className="font-label text-[9px] tracking-[0.18em] uppercase text-[rgba(11,12,13,0.5)]">
          {formatDate(order.created_at)}
        </time>
      </div>
      <p className="inline-flex items-center gap-1.5 font-body text-[rgba(11,12,13,0.65)] text-sm mt-1">
        <Phone className="w-3.5 h-3.5 text-[#8A6D33]" strokeWidth={1.8} /> {order.phone}
      </p>

      <div className="mt-4 grid sm:grid-cols-3 gap-2.5">
        {[
          { role: "Loaf", item: order.left_item },
          { role: "Pairing One", item: order.right_items?.[0] },
          { role: "Pairing Two", item: order.right_items?.[1] },
        ].map(({ role, item }) =>
          item ? (
            <div key={role} className="flex items-center gap-2.5 border border-[rgba(179,145,74,0.3)] rounded-[10px] p-2 bg-[rgba(179,145,74,0.05)]">
              <img src={item.image_url} alt={item.name} className="w-9 h-9 rounded-[7px] object-cover shrink-0" loading="lazy" />
              <div className="min-w-0">
                <p className="font-label text-[7.5px] tracking-[0.18em] uppercase text-[rgba(11,12,13,0.45)]">{role}</p>
                <p className="font-body text-[var(--hoa-ink-950)] text-xs leading-snug truncate">{item.name}</p>
              </div>
            </div>
          ) : null
        )}
      </div>

      {order.notes && (
        <p className="flex items-start gap-1.5 font-body text-[rgba(11,12,13,0.6)] text-xs leading-relaxed mt-3 italic">
          <StickyNote className="w-3.5 h-3.5 text-[#8A6D33] mt-0.5 shrink-0" strokeWidth={1.8} />
          {order.notes}
        </p>
      )}
    </article>
  );
}

/* -------------------------------------------------------- PatronCard */
function PatronCard({ patron, onOpen }) {
  return (
    <button
      data-testid={`patron-card-${patron.phone}`}
      onClick={() => onOpen(patron)}
      className="w-full text-left bg-[var(--hoa-cream-50)] rounded-[var(--hoa-radius-lg)] shadow-[var(--hoa-shadow-card)] p-5 sm:p-6 hover:brightness-[1.03] transition-[filter]"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-[var(--hoa-ink-950)] text-lg sm:text-xl">{patron.customer_name}</h2>
        <span className="font-label text-[9px] tracking-[0.18em] uppercase text-[rgba(11,12,13,0.5)]">
          {patron.order_count} {patron.order_count === 1 ? "order" : "orders"}
        </span>
      </div>
      <p className="inline-flex items-center gap-1.5 font-body text-[rgba(11,12,13,0.65)] text-sm mt-1">
        <Phone className="w-3.5 h-3.5 text-[#8A6D33]" strokeWidth={1.8} /> {patron.phone}
      </p>
      {patron.last_order_at && (
        <p className="font-body text-[rgba(11,12,13,0.5)] text-xs mt-2">
          Last order · {formatDate(patron.last_order_at)}
        </p>
      )}
    </button>
  );
}

/* ------------------------------------------------------- PatronPanel */
function PatronPanel({ patron, orders, loading, onClose }) {
  return (
    <div
      className="mt-8 border border-[rgba(179,145,74,0.45)] rounded-[var(--hoa-radius-lg)] p-5 sm:p-6 bg-[rgba(240,233,216,0.03)]"
      data-testid="patron-orders-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-label text-[9px] tracking-[0.22em] uppercase hoa-gold-text">Patron</p>
          <h2 className="font-display text-[var(--hoa-cream-50)] text-2xl mt-1">{patron.customer_name}</h2>
          <p className="inline-flex items-center gap-1.5 font-body text-[rgba(240,233,216,0.6)] text-sm mt-1">
            <Phone className="w-3.5 h-3.5 text-[var(--hoa-gold-400)]" strokeWidth={1.8} /> {patron.phone}
            <span className="mx-1">·</span>
            {patron.order_count} {patron.order_count === 1 ? "order" : "orders"}
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="patron-panel-close"
          className="text-[rgba(240,233,216,0.55)] hover:text-[var(--hoa-cream-50)] transition-colors"
          aria-label="Close patron view"
        >
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>
      </div>
      <div className="mt-5 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--hoa-gold-400)]" />
          </div>
        ) : (
          orders.map((o) => <OrderCard key={o.id} order={o} />)
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------- LedgerControls */
function LedgerControls({ search, onSearch, onClear }) {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgba(240,233,216,0.4)]" strokeWidth={1.8} />
        <input
          data-testid="ledger-search-input"
          className={`${inputCls} h-10 pl-11 rounded-full`}
          placeholder="Search by name or phone…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {search && (
        <button
          data-testid="ledger-clear-filters"
          onClick={onClear}
          className="inline-flex items-center gap-1 h-10 px-4 rounded-full border border-[rgba(179,145,74,0.4)] font-label text-[9px] tracking-[0.18em] uppercase text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-cream-50)] transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.8} /> Clear
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------- LedgerList */
function LedgerList({ loading, error, view, orders, customers, filtered, onRetry, onOpenPatron }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="orders-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-20" data-testid="orders-error">
        <p className="font-body text-[rgba(240,233,216,0.7)]">The ledger could not be loaded.</p>
        <button
          onClick={onRetry}
          data-testid="orders-retry-button"
          className="mt-5 font-label text-[11px] tracking-[0.18em] uppercase h-11 px-6 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] hover:bg-[rgba(240,233,216,0.06)] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  if (view === "orders") {
    if (orders.length === 0) {
      return (
        <div className="text-center py-20 border border-dashed border-[rgba(179,145,74,0.35)] rounded-[var(--hoa-radius-lg)]" data-testid="orders-empty-state">
          <p className="font-display text-[var(--hoa-cream-50)] text-xl">
            {filtered ? "Nothing matches that." : "The ledger is empty."}
          </p>
          <p className="font-body text-[rgba(240,233,216,0.55)] text-sm mt-2">
            {filtered ? "Try a different name, number or date." : "When a box is built, it will appear here."}
          </p>
        </div>
      );
    }
    return orders.map((order) => <OrderCard key={order.id} order={order} />);
  }
  if (customers.length === 0) {
    return (
      <div className="text-center py-20 border border-dashed border-[rgba(179,145,74,0.35)] rounded-[var(--hoa-radius-lg)]" data-testid="patrons-empty-state">
        <p className="font-display text-[var(--hoa-cream-50)] text-xl">
          {filtered ? "No patron by that name." : "No patrons yet."}
        </p>
      </div>
    );
  }
  return customers.map((c) => <PatronCard key={c.phone} patron={c} onOpen={onOpenPatron} />);
}

/* --------------------------------------------------------- ViewStats */
function ViewStats({ stats }) {
  const items = [
    { label: "Today", value: stats.today },
    { label: "This Week", value: stats.this_week },
    { label: "This Month", value: stats.this_month },
    { label: "This Year", value: stats.this_year },
  ];
  return (
    <div className="mt-10" data-testid="ledger-view-stats">
      <div className="flex items-center gap-4">
        <span className="font-label text-[9px] tracking-[0.26em] uppercase text-[rgba(240,233,216,0.5)]">
          Website Views
        </span>
        <span className="flex-1 h-px bg-[rgba(179,145,74,0.2)]" />
        <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.4)]">
          {stats.total} all time
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map(({ label, value }) => (
          <div
            key={label}
            data-testid={`view-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="border border-[rgba(179,145,74,0.35)] rounded-[var(--hoa-radius-md)] bg-[rgba(240,233,216,0.04)] p-4"
          >
            <p className="font-label text-[8px] tracking-[0.24em] uppercase text-[rgba(240,233,216,0.5)]">{label}</p>
            <p className="font-display hoa-gold-text text-2xl sm:text-3xl mt-1">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- OrdersAdmin */
export default function OrdersAdmin() {
  const [authed, setAuthed] = useState(null);
  const [search, setSearch] = useState("");
  const [bakes, setBakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stats, setStats] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const goLedgerHome = useCallback(() => {
    setBuilderOpen(false);
    setMasterOpen(false);
    setAccountsOpen(false);
    setHistoryKey((k) => k + 1);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/admin/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const handleAuthError = useCallback((e) => {
    if (e?.response?.status === 401) setAuthed(false);
    else setError(true);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/admin/bake-orders");
      setBakes(res.data || []);
    } catch (e) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!authed || builderOpen) return undefined;
    fetchData();
    return undefined;
  }, [authed, builderOpen, fetchData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [builderOpen]);

  useEffect(() => {
    if (!authed) return;
    api
      .get("/admin/stats/views")
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, [authed]);

  const handleLogout = useCallback(async () => {
    try {
      await api.post("/admin/logout");
    } catch {
      /* cookie clear is best-effort */
    }
    setAuthed(false);
  }, []);

  return (
    <main className="hoa-texture hoa-grain min-h-screen" data-testid="orders-admin-page">
      <div className="relative z-[1] mx-auto w-full max-w-[860px] px-5 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link
              to="/"
              data-testid="orders-back-home-link"
              className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.65)] hover:text-[var(--hoa-gold-400)] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Back to the house
            </Link>
            {authed && (
              <button
                onClick={goLedgerHome}
                data-testid="header-ledger-link"
                className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.65)] hover:text-[var(--hoa-gold-400)] transition-colors"
              >
                <ScrollText className="w-4 h-4" strokeWidth={1.6} /> Ledger
              </button>
            )}
          </div>
          <MiniMark />
        </div>

        {authed === null ? (
          <div className="flex items-center justify-center py-40" data-testid="ledger-auth-checking">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
          </div>
        ) : !authed ? (
          <LoginView onLogin={() => setAuthed(true)} />
        ) : accountsOpen ? (
          <>
            <div className="mt-10 flex items-center gap-4">
              <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
                Accounts
              </span>
              <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
              <button
                onClick={() => setAccountsOpen(false)}
                data-testid="accounts-close-button"
                className="inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.55)] hover:text-[var(--hoa-gold-400)] transition-colors"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.6} /> Close
              </button>
            </div>
            <h1 className="font-display text-[var(--hoa-cream-50)] text-3xl sm:text-4xl mt-4">The account book</h1>
            <p className="font-body text-[rgba(240,233,216,0.6)] text-sm sm:text-base mt-3">
              Note every rupee in and out — the house keeps the running balance.
            </p>
            <Accounts onAuthError={handleAuthError} />
          </>
        ) : masterOpen ? (
          <>
            <div className="mt-10 flex items-center gap-4">
              <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
                Master List
              </span>
              <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
              <button
                onClick={() => setMasterOpen(false)}
                data-testid="master-close-button"
                className="inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.55)] hover:text-[var(--hoa-gold-400)] transition-colors"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.6} /> Close
              </button>
            </div>
            <h1 className="font-display text-[var(--hoa-cream-50)] text-3xl sm:text-4xl mt-4">Master products</h1>
            <p className="font-body text-[rgba(240,233,216,0.6)] text-sm sm:text-base mt-3">
              Switch a bake off and it disappears from every list until you switch it back on.
            </p>
            <MasterProducts onAuthError={handleAuthError} />
          </>
        ) : builderOpen ? (
          <>
            <div className="mt-10 flex items-center gap-4">
              <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
                New Order
              </span>
              <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
              <button
                onClick={() => setBuilderOpen(false)}
                data-testid="bake-close-button"
                className="inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.55)] hover:text-[var(--hoa-gold-400)] transition-colors"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.6} /> Close
              </button>
            </div>
            <h1 className="font-display text-[var(--hoa-cream-50)] text-3xl sm:text-4xl mt-4">Add an order</h1>
            <p className="font-body text-[rgba(240,233,216,0.6)] text-sm sm:text-base mt-3">
              Pick the bakes — the house will tally your ingredients and making cost.
            </p>
            <BakeOrderBuilder onAuthError={handleAuthError} />
          </>
        ) : (
          <>
            <div className="mt-10 flex items-center gap-4">
              <span className="font-label text-[10px] sm:text-[11px] tracking-[0.3em] uppercase hoa-gold-text">
                The Ledger
              </span>
              <span className="flex-1 h-px bg-[rgba(179,145,74,0.35)]" />
              <button
                onClick={handleLogout}
                data-testid="ledger-logout-button"
                className="inline-flex items-center gap-1.5 font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.55)] hover:text-[var(--hoa-gold-400)] transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} /> Sign out
              </button>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="ledger-add-order-button"
                onClick={() => setBuilderOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:brightness-110 transition-[filter]"
              >
                <PlusCircle className="w-4 h-4" strokeWidth={1.8} /> Add Order
              </button>
              <button
                data-testid="ledger-master-list-button"
                onClick={() => setMasterOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] font-label text-[11px] tracking-[0.2em] uppercase hover:bg-[rgba(240,233,216,0.06)] transition-colors"
              >
                <ListChecks className="w-4 h-4" strokeWidth={1.8} /> Master List
              </button>
              <button
                data-testid="ledger-accounts-button"
                onClick={() => setAccountsOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] font-label text-[11px] tracking-[0.2em] uppercase hover:bg-[rgba(240,233,216,0.06)] transition-colors"
              >
                <Wallet className="w-4 h-4" strokeWidth={1.8} /> Accounts
              </button>
              <MenuPdfButton onAuthError={handleAuthError} />
              <RecipeBookButton onAuthError={handleAuthError} />
            </div>
            <h1 className="font-display text-[var(--hoa-cream-50)] text-3xl sm:text-4xl mt-8">Orders</h1>
            <p className="font-body text-[rgba(240,233,216,0.6)] text-sm sm:text-base mt-3">
              Every order built by the house, newest first. Tap one to reopen it or send its invoice.
            </p>

            {stats && <ViewStats stats={stats} />}

            <LedgerControls search={search} onSearch={setSearch} onClear={() => setSearch("")} />

            <div className="mt-8 space-y-4" data-testid="orders-admin-table">
              <BakeHistory
                key={historyKey}
                orders={bakes}
                loading={loading}
                error={error}
                search={search}
                onRetry={fetchData}
                onAuthError={handleAuthError}
              />
            </div>
          </>
        )}
      </div>
    </main>
  );
}
